/**
 * RoomSelectionFormContainer wires lobby signals to the RoomSelectionForm UI.
 *
 * Why: The form needs reactive state from lobby signals. This container
 * handles the signal subscriptions and passes plain values as props.
 *
 * When to use: Used by App.tsx when user is selecting/joining a room.
 */
import { useState, useEffect } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { batch } from '@preact/signals-react'
import { RoomSelectionForm } from './RoomSelectionForm'
import type { RoomDep } from '../../../deps/room/room.types'
import type { Lobby } from '../../../deps/lobby/lobby.types'
import type { AuthDep } from '../../../deps/auth/auth.types'
import type { LocalMates } from '../../../deps/localMates/localMates/localMates.types'

interface RoomSelectionFormContainerProps {
  roomConnection: RoomDep['connection']
  lobby: Lobby
  auth: AuthDep
  localMates: LocalMates
}

export function RoomSelectionFormContainer({ roomConnection, lobby, auth, localMates }: RoomSelectionFormContainerProps) {
  useSignals()

  const isSignedIn = auth.isLoaded.value && !!auth.currentUser.value
  const [rooms, setRooms] = useState<Array<{ roomId: string; roomCode: string; displayName: string; imageUrl?: string }>>([])
  const [isLoadingRooms, setIsLoadingRooms] = useState(false)

  async function loadRooms() {
    if (!isSignedIn) {
      setRooms([])
      return
    }
    setIsLoadingRooms(true)
    try {
      const token = await auth.getToken()
      const workerUrl = roomConnection._workerUrl || ''
      const headers: Record<string, string> = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const res = await fetch(`${workerUrl}/api/rooms`, { headers })
      if (!res.ok) throw new Error('Failed to load rooms')

      const data = await res.json()
      if (data.success && Array.isArray(data.rooms)) {
        setRooms(data.rooms)
      }
    } catch (e) {
      console.error('Error fetching owned rooms:', e)
    } finally {
      setIsLoadingRooms(false)
    }
  }

  useEffect(() => {
    loadRooms()
  }, [isSignedIn, auth, roomConnection._workerUrl])

  const handleDeleteRoom = async (code: string) => {
    const ok = window.confirm(`Are you sure you want to delete room ${code}?`)
    if (!ok) return
    try {
      await roomConnection.deleteRoom(code)
      await loadRooms()
    } catch (e) {
      console.error('Failed to delete room:', e)
    }
  }

  const handleJoin = (code: string) => {
    if (!code) return

    if (!localMates.localMates.value[0]?.name) {
      lobby.pendingRoomEntryAction.value = () => handleJoin(code)
      lobby.roomMode.value = 'name-entry'
      return
    }

    batch(() => {
      lobby.pendingRoomEntryAction.value = null
      roomConnection.clearError()
      lobby.roomMode.value = 'room'
      roomConnection.joinRoom(code)
    })
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', `/r/${code}`)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  return (
    <RoomSelectionForm
      roomName={lobby.joinRoomName.value || ''}
      error={roomConnection.error.value || ''}
      onRoomNameChange={(name) => lobby.joinRoomName.value = name}
      onJoin={() => handleJoin(lobby.joinRoomName.value)}
      onCreate={() => lobby.roomMode.value = 'room-creation'}
      isSignedIn={isSignedIn}
      onSignIn={auth.signIn}
      ownedRooms={rooms}
      isLoadingRooms={isLoadingRooms}
      onJoinRoomCode={handleJoin}
      onDeleteRoomCode={handleDeleteRoom}
    />
  )
}
